import {
  Box,
  ButtonGroup,
  Circle,
  Divider,
  HStack,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import React from "react";
import { useDebounce } from "use-debounce";
import RegistryGridSearch from "../../routes/registries/components/RegistryGridSearch";
import { useSetIsAddMode } from "../../routes/registries/stores/actions/useSetIsAddMode";
import { useSetIsEditMode } from "../../routes/registries/stores/actions/useSetIsEditMode";
import { useToggleCaseView } from "../../routes/registries/stores/actions/useToggleCaseView";
import { useToggleGeneMapView } from "../../routes/registries/stores/actions/useToggleGeneMapView";
import { useTogglePaneOpen } from "../../routes/registries/stores/actions/useTogglePaneOpen";
import { useSelectIsAddMode } from "../../routes/registries/stores/select/useSelectIsAddMode";
import { useSelectIsCaseView } from "../../routes/registries/stores/select/useSelectIsCaseView";
import { useSelectIsEditMode } from "../../routes/registries/stores/select/useSelectIsEditMode";
import { useSelectIsGeneMapView } from "../../routes/registries/stores/select/useSelectIsGeneMapView";
import { useSelectIsPaneOpen } from "../../routes/registries/stores/select/useSelectIsPaneOpen";

import { MaterialIcon } from "../shared/MaterialIcon";

interface IProps {
  gridRef: any;
  isAllowEdit?: boolean;
  isAllowAdd?: boolean;
  isAllowGeneMap?: boolean;
}

const RegistryToolbar = ({
  gridRef,
  isAllowEdit = true,
  isAllowAdd = true,
  isAllowGeneMap = true,
}: IProps) => {
  const [registrySearch, setRegistrySearch] = React.useState("");

  const [debouncedTextSearch] = useDebounce(registrySearch, 300);
  const [isAnyFilterPresent, setIsAnyFilterPresent] = React.useState(false);

  const toast = useToast();

  const togglePaneOpen = useTogglePaneOpen();
  const toggleGeneMapView = useToggleGeneMapView();
  const toggleCaseView = useToggleCaseView();
  const setIsEditMode = useSetIsEditMode();
  const setIsAddMode = useSetIsAddMode();
  const isPaneOpen = useSelectIsPaneOpen();
  const isGeneMapView = useSelectIsGeneMapView();
  const isCaseView = useSelectIsCaseView();
  const isEditMode = useSelectIsEditMode();
  const isAddMode = useSelectIsAddMode();

  const handleSetIsEditMode = () => {
    setIsEditMode({ isEditMode: true });
  };

  const handleSetIsAddMode = () => {
    setIsAddMode({ isAddMode: true });
  };

  const onFilterChanged = React.useCallback(() => {
    let isAnyFilterPresentUpdate = false;
    const gridApi = gridRef?.current?.api;
    if (gridApi) {
      isAnyFilterPresentUpdate = gridApi.isAnyFilterPresent();
    }
    setIsAnyFilterPresent(isAnyFilterPresentUpdate);
  }, [gridRef]);

  const handleOnRemoveFilters = () => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.setFilterModel(null);

      setRegistrySearch("");
      toast({
        title: "Removed filters",
        description: ``,
        status: "info",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleIsToggleColumns = () => {
    if (gridRef?.current) {
      const openToolPanel = gridRef.current.api.getOpenedToolPanel();

      if (openToolPanel === "columns") {
        gridRef.current.api.closeToolPanel("columns");
        gridRef.current.api.setSideBarVisible(false);
      } else if (openToolPanel === "filters") {
        // Switch to columns
        gridRef.current.api.openToolPanel("columns");
      } else {
        gridRef.current.api.setSideBarVisible(true);
        gridRef.current.api.openToolPanel("columns");
      }
    }
  };

  const handleIsToggleFilters = () => {
    if (gridRef?.current) {
      const openToolPanel = gridRef.current.api.getOpenedToolPanel();

      if (openToolPanel === "filters") {
        gridRef.current.api.closeToolPanel("filters");
        gridRef.current.api.setSideBarVisible(false);
      } else if (openToolPanel === "columns") {
        // Switch to filters
        gridRef.current.api.openToolPanel("filters");
      } else {
        gridRef.current.api.setSideBarVisible(true);
        gridRef.current.api.openToolPanel("filters");
      }
    }
  };

  React.useEffect(() => {
    const gridApi = gridRef?.current?.api;

    if (gridApi) {
      gridApi.addEventListener("filterChanged", onFilterChanged);
    }

    return () => {
      if (gridApi) {
        gridApi.removeEventListener("filterChanged", onFilterChanged);
      }
    };
  }, [onFilterChanged, gridRef]);

  React.useEffect(() => {
    if (gridRef?.current) {
      const gridApi = gridRef?.current?.api;
      gridApi.setQuickFilter(debouncedTextSearch);
    }
  }, [gridRef, debouncedTextSearch]);

  return (
    <HStack justifyContent={"space-between"} px={"4"}>
      <ButtonGroup
        variant={"ghost"}
        spacing={6}
        colorScheme={"gray"}
        alignItems="center"
        isDisabled={isEditMode || isAddMode}
        // colorScheme="whiteAlpha"
      >
        {isAnyFilterPresent && (
          <Tooltip label="Clear Filters">
            <IconButton
              onClick={handleOnRemoveFilters}
              colorScheme="red"
              icon={
                <MaterialIcon
                  name="filter_alt_off"
                  fontSize="1.5rem"
                  variant="outline"
                />
              }
              aria-label="remove filters columns"
            ></IconButton>
          </Tooltip>
        )}

        <Tooltip label="Edit columns">
          <IconButton
            onClick={handleIsToggleColumns}
            icon={
              <MaterialIcon
                name="view_column"
                fontSize="1.5rem"
                variant="outline"
              />
            }
            aria-label="edit columns"
          ></IconButton>
        </Tooltip>
        <Tooltip label="View filters">
          <IconButton
            onClick={handleIsToggleFilters}
            icon={
              <MaterialIcon
                name="filter_alt"
                fontSize="1.5rem"
                variant="outline"
              />
            }
            aria-label="view filters"
          ></IconButton>
        </Tooltip>

        {(isAllowAdd || isAllowEdit) && (
          <Divider
            h="1.5rem"
            w="2px"
            bg="bg.highest"
            orientation="vertical"
          ></Divider>
        )}

        {isAllowAdd && (
          <Tooltip label="Add records">
            <IconButton
              onClick={handleSetIsAddMode}
              icon={
                <MaterialIcon
                  name="group_add"
                  fontSize="1.5rem"
                  variant="outline"
                />
              }
              aria-label="add records"
            ></IconButton>
          </Tooltip>
        )}

        {isAllowEdit && (
          <Tooltip label="Edit records">
            <IconButton
              onClick={handleSetIsEditMode}
              icon={
                <MaterialIcon
                  name="mode_edit"
                  fontSize="1.5rem"
                  variant="outline"
                />
              }
              aria-label="edit data"
            ></IconButton>
          </Tooltip>
        )}

        <Divider
          h="1.5rem"
          w="2px"
          bg="bg.highest"
          orientation="vertical"
        ></Divider>

        <Box position={"relative"}>
          <Tooltip label="View statistics">
            <IconButton
              onClick={togglePaneOpen}
              colorScheme={"gray"}
              icon={
                <MaterialIcon
                  name="auto_graph"
                  fontSize="1.5rem"
                  variant="outline"
                />
              }
              aria-label="view statistics"
            ></IconButton>
          </Tooltip>
          {isPaneOpen && (
            <Circle
              position={"absolute"}
              zIndex="1"
              top="0"
              right="0"
              bg="red.400"
              color="white"
            >
              <MaterialIcon name="cancel" fontSize="1rem" variant="outline" />
            </Circle>
          )}
        </Box>

        {isAllowGeneMap && (
          <Box position={"relative"}>
            <Tooltip label={isGeneMapView ? "Close gene map" : "Open gene map"}>
              <IconButton
                onClick={toggleGeneMapView}
                colorScheme={"gray"}
                icon={
                  <MaterialIcon
                    name="biotech"
                    fontSize="1.5rem"
                    variant="outline"
                  />
                }
                aria-label={isGeneMapView ? "close gene map" : "view gene map"}
              ></IconButton>
            </Tooltip>
            {isGeneMapView && (
              <Circle
                position={"absolute"}
                zIndex="1"
                top="0"
                right="0"
                bg="red.400"
                color="white"
              >
                <MaterialIcon name="cancel" fontSize="1rem" variant="outline" />
              </Circle>
            )}
          </Box>
        )}

        <Box position={"relative"}>
          <Tooltip label="View case summary">
            <IconButton
              onClick={toggleCaseView}
              colorScheme={"gray"}
              icon={
                <MaterialIcon
                  name="date_range"
                  fontSize="1.5rem"
                  variant="outline"
                />
              }
              aria-label={isCaseView ? "close case view" : "view case"}
            ></IconButton>
          </Tooltip>
          {isCaseView && (
            <Circle
              position={"absolute"}
              zIndex="1"
              top="0"
              right="0"
              bg="red.400"
              color="white"
            >
              <MaterialIcon name="cancel" fontSize="1rem" variant="outline" />
            </Circle>
          )}
        </Box>

        <Divider
          h="1.5rem"
          w="2px"
          bg="bg.highest"
          orientation="vertical"
        ></Divider>
        <RegistryGridSearch
          search={registrySearch}
          setSearch={setRegistrySearch}
        ></RegistryGridSearch>
      </ButtonGroup>
    </HStack>
  );
};

export default RegistryToolbar;
